import React, { useMemo, useState, useEffect } from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler } from 'chart.js';
import { useApp } from '../../context/AppContext';
import TransactionModal from '../Transactions/TransactionModal';
import TransferModal from '../Transfers/TransferModal';
import { formatCurrency, formatMoney, formatDate, monthNames } from '../../utils/formatters';
import FinancialHealth from './FinancialHealth';
import Projection from './Projection';
import AutoInsights from './AutoInsights';
import MonthSelector from '../shared/MonthSelector';

function SavingsGoalWidget() {
  const { state, dispatch, monthIncome, monthExpense } = useApp();
  const [editing, setEditing] = useState(false);
  const [goalInput, setGoalInput] = useState('');

  const saved = Math.max(0, monthIncome - monthExpense);
  const goal = state.savingsGoalMonthly || 0;
  const pct = goal > 0 ? Math.min(100, Math.round(saved / goal * 100)) : 0;
  const color = pct >= 100 ? '#1d9e75' : pct >= 60 ? '#c9a227' : '#e24b4a';
  const msg = !goal ? 'Defina uma meta para acompanhar sua economia mensal.'
    : pct >= 100 ? 'Meta atingida! Parabéns!'
    : pct >= 60 ? `Faltam ${formatCurrency(goal - saved)} para a meta.`
    : `Você está em ${pct}% da sua meta mensal.`;

  const saveGoal = () => {
    const v = parseFloat(goalInput);
    if (!isNaN(v) && v > 0) dispatch({ type: 'SET_SAVINGS_GOAL', payload: v });
    setEditing(false);
  };

  return (
    <div className="dash-card" style={{ padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div className="dash-card-title">Meta de Economia</div>
          <div className="dash-card-sub">Economia do mês atual</div>
        </div>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="fas fa-piggy-bank" style={{ color, fontSize: 15 }}></i>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 26, fontWeight: 800, color }}>{formatCurrency(saved)}</span>
        {goal > 0 && <span style={{ fontSize: 13, color: 'var(--muted)' }}>de {formatCurrency(goal)}</span>}
      </div>

      {goal > 0 && (
        <div style={{ height: 8, background: 'var(--surface2)', borderRadius: 99, overflow: 'hidden', marginBottom: 8 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width 0.6s ease' }} />
        </div>
      )}

      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>{msg}</div>

      {editing ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            className="form-control"
            type="number" min="1" step="100"
            placeholder="Meta mensal (R$)"
            value={goalInput}
            onChange={e => setGoalInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveGoal(); if (e.key === 'Escape') setEditing(false); }}
            autoFocus
            style={{ flex: 1, padding: '6px 10px', fontSize: 13 }}
          />
          <button className="btn btn-primary" onClick={saveGoal} style={{ padding: '6px 12px', fontSize: 12 }}>
            <i className="fas fa-check"></i>
          </button>
          <button className="btn btn-ghost" onClick={() => setEditing(false)} style={{ padding: '6px 10px', fontSize: 12 }}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      ) : (
        <button
          onClick={() => { setGoalInput(goal ? String(goal) : ''); setEditing(true); }}
          style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 14px', fontSize: 12, color: 'var(--muted)', cursor: 'pointer', fontWeight: 500 }}
        >
          <i className="fas fa-pen" style={{ marginRight: 6, fontSize: 10 }}></i>
          {goal ? 'Alterar meta' : 'Definir meta'}
        </button>
      )}
    </div>
  );
}

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler);

// ── Gamificação: Streak + Conquistas ─────────────────────────────────────────

function calcStreak(transactions) {
  if (!transactions.length) return 0;
  const txDates = new Set(transactions.map(t => t.date.slice(0, 10)));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().slice(0, 10);
  const yesterStr = new Date(today - 86400000).toISOString().slice(0, 10);
  if (!txDates.has(todayStr) && !txDates.has(yesterStr)) return 0;
  let d = txDates.has(todayStr) ? new Date(today) : new Date(today - 86400000);
  let streak = 0;
  while (txDates.has(d.toISOString().slice(0, 10))) {
    streak++;
    d = new Date(d - 86400000);
  }
  return streak;
}

const BADGES = [
  { id: 'first_tx',     emoji: '🎯', label: 'Primeiro Passo',    desc: 'Registrou o primeiro lançamento',             check: s => s.transactions.length > 0 },
  { id: 'streak3',      emoji: '🔥', label: '3 Dias em Chama',   desc: '3 dias consecutivos com lançamentos',          check: (s, st) => st >= 3 },
  { id: 'streak7',      emoji: '⚡', label: 'Semana Consistente', desc: '7 dias seguidos com lançamentos',              check: (s, st) => st >= 7 },
  { id: 'streak30',     emoji: '💎', label: 'Mês Perfeito',      desc: '30 dias seguidos com lançamentos',             check: (s, st) => st >= 30 },
  { id: 'investor',     emoji: '📈', label: 'Investidor',        desc: 'Cadastrou o primeiro investimento',            check: s => s.investments.length > 0 },
  { id: 'goal_done',    emoji: '🏆', label: 'Meta Alcançada',    desc: 'Concluiu uma meta financeira',                 check: s => s.goals.some(g => g.completed || (g.current || 0) >= (g.target || Infinity)) },
  { id: 'budget_maker', emoji: '📊', label: 'Orçamentista',     desc: 'Criou orçamentos para 3+ categorias',         check: s => s.budgets.filter(b => (b.planned || b.limit || 0) > 0).length >= 3 },
  { id: 'debt_free',    emoji: '🎉', label: 'Livre de Dívidas',  desc: 'Quitou todas as dívidas cadastradas',         check: s => s.debts.length > 0 && s.debts.every(d => d.settled) },
  { id: 'tx50',         emoji: '⭐', label: 'Registrador',       desc: '50 lançamentos registrados',                  check: s => s.transactions.length >= 50 },
  { id: 'tx200',        emoji: '🌟', label: 'Mestre das Finanças', desc: '200 lançamentos registrados',               check: s => s.transactions.length >= 200 },
  { id: 'diversified',  emoji: '🌈', label: 'Diversificado',    desc: 'Usou 10+ categorias diferentes',              check: s => new Set(s.transactions.map(t => t.categoryId)).size >= 10 },
  { id: 'saver',        emoji: '💰', label: 'Poupador',          desc: 'Poupou mais de 20% da renda num mês',         check: s => {
    const now = new Date();
    const m = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
    const txs = s.transactions.filter(t => t.date.startsWith(m) && t.status === 'paid' && !t.cardId);
    const inc = txs.filter(t => t.type==='income').reduce((a,t)=>a+t.value,0);
    const exp = txs.filter(t => t.type==='expense').reduce((a,t)=>a+t.value,0);
    return inc > 0 && (inc - exp) / inc >= 0.2;
  }},
];

// XP calculado direto dos dados — sem estado extra para sincronizar
function calcXP(state, streak, earnedCount) {
  return state.transactions.length * 5
    + earnedCount * 100
    + streak * 10
    + state.goals.length * 30
    + state.budgets.length * 20
    + state.investments.length * 30
    + (state.vehicles?.length || 0) * 40
    + state.accounts.length * 25;
}

function GamificationCard() {
  const { state } = useApp();

  const { streak, badges } = useMemo(() => {
    const streak = calcStreak(state.transactions);
    const badges = BADGES.map(b => ({ ...b, earned: b.check(state, streak) }));
    return { streak, badges };
  }, [state]);

  const earned = badges.filter(b => b.earned);
  const locked = badges.filter(b => !b.earned);

  // Nível: progressivo — nível n vai de 100·(n-1)² até 100·n² XP
  const xp = calcXP(state, streak, earned.length);
  const level = Math.floor(Math.sqrt(xp / 100)) + 1;
  const levelStart = 100 * (level - 1) * (level - 1);
  const nextLevelXP = 100 * level * level;
  const levelPct = Math.max(0, Math.min(100, Math.round(((xp - levelStart) / (nextLevelXP - levelStart)) * 100)));

  const streakColor = streak >= 30 ? '#8A05BE' : streak >= 7 ? '#c9a227' : streak >= 3 ? '#e24b4a' : '#378ADD';
  const streakBg    = streak >= 30 ? '#f3e8ff' : streak >= 7 ? '#fef9ec' : streak >= 3 ? '#fee2e2' : '#dbeafe';
  const streakLabel = streak >= 30 ? 'Incrível!' : streak >= 7 ? 'Excelente!' : streak >= 3 ? 'Ótimo!' : streak >= 1 ? 'Começando!' : 'Sem streak';

  return (
    <div className="dash-card" style={{ padding: '18px 20px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <div className="dash-card-title">Jornada</div>
          <div className="dash-card-sub">{earned.length} de {badges.length} conquistas desbloqueadas</div>
        </div>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: streakBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
          🔥
        </div>
      </div>

      {/* Banner XP / Nível / Ofensiva */}
      <div style={{ background: 'linear-gradient(135deg, var(--navy), #1e3a52)', borderRadius: 14, padding: '14px 18px', marginBottom: 12, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        {[
          { emoji: '🔥', value: streak, label: 'OFENSIVA' },
          { emoji: '⭐', value: xp.toLocaleString('pt-BR'), label: 'XP TOTAL' },
          { emoji: '👑', value: level, label: 'NÍVEL' },
        ].map(item => (
          <div key={item.label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#fff' }}>
              <span style={{ marginRight: 5 }}>{item.emoji}</span>{item.value}
            </div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.55)', fontWeight: 700, letterSpacing: 1.5, marginTop: 2 }}>{item.label}</div>
          </div>
        ))}
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'rgba(255,255,255,0.55)', fontWeight: 600, marginBottom: 4 }}>
            <span>Nível {level}</span>
            <span>{xp.toLocaleString('pt-BR')} / {nextLevelXP.toLocaleString('pt-BR')} XP</span>
          </div>
          <div style={{ height: 6, borderRadius: 99, background: 'rgba(255,255,255,0.15)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${levelPct}%`, background: 'var(--gold)', borderRadius: 99, transition: 'width 0.5s ease' }} />
          </div>
        </div>
      </div>

      {/* Streak banner */}
      <div style={{ background: streakBg, border: `1px solid ${streakColor}30`, borderRadius: 14, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ textAlign: 'center', minWidth: 56 }}>
          <div style={{ fontSize: 36, fontWeight: 900, color: streakColor, lineHeight: 1 }}>{streak}</div>
          <div style={{ fontSize: 10, color: streakColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>dias</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: streakColor }}>{streak > 0 ? `🔥 ${streakLabel}` : '💤 Sem streak ativo'}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
            {streak > 0
              ? `Você registra lançamentos há ${streak} dia${streak !== 1 ? 's' : ''} seguido${streak !== 1 ? 's' : ''}!`
              : 'Registre um lançamento hoje para iniciar seu streak'}
          </div>
          {streak > 0 && (
            <div style={{ display: 'flex', gap: 3, marginTop: 8 }}>
              {Array.from({ length: Math.min(streak, 14) }).map((_, i) => (
                <div key={i} style={{ width: 10, height: 10, borderRadius: 3, background: streakColor, opacity: 1 - (Math.min(streak, 14) - 1 - i) * 0.05 }} />
              ))}
              {streak > 14 && <div style={{ fontSize: 10, color: streakColor, fontWeight: 700, paddingLeft: 2 }}>+{streak - 14}</div>}
            </div>
          )}
        </div>
      </div>

      {/* Conquistas earned */}
      {earned.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
            Desbloqueadas ({earned.length})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: 16 }}>
            {earned.map(b => (
              <div key={b.id} title={b.desc} style={{ background: 'linear-gradient(135deg, #fef9ec, #fff7d4)', border: '1px solid #c9a22740', borderRadius: 12, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 22 }}>{b.emoji}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2 }}>{b.label}</div>
                  <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 2, lineHeight: 1.3 }}>{b.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Conquistas locked */}
      {locked.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
            Bloqueadas ({locked.length})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {locked.map(b => (
              <div key={b.id} title={b.desc} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, opacity: 0.55 }}>
                <span style={{ fontSize: 22, filter: 'grayscale(1)' }}>{b.emoji}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2 }}>{b.label}</div>
                  <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 2, lineHeight: 1.3 }}>{b.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function MonthComparisonCard() {
  const { state, currentMonth } = useApp();

  const { rows, prevMonthLabel, hasData } = useMemo(() => {
    const [cy, cm] = currentMonth.split('-').map(Number);
    const curM  = currentMonth;
    const prevD = new Date(cy, cm - 2, 1);
    const prevM = `${prevD.getFullYear()}-${String(prevD.getMonth() + 1).padStart(2, '0')}`;
    const prevMonthLabel = prevD.toLocaleDateString('pt-BR', { month: 'long' });

    const sumByCat = (month) => {
      const map = {};
      state.transactions
        .filter(t => t.date.startsWith(month) && t.type === 'expense' && t.status === 'paid' && !t.cardId)
        .forEach(t => { map[t.categoryId] = (map[t.categoryId] || 0) + t.value; });
      return map;
    };

    const cur  = sumByCat(curM);
    const prev = sumByCat(prevM);
    const allCats = new Set([...Object.keys(cur), ...Object.keys(prev)]);

    const rows = [...allCats]
      .map(id => {
        const curVal  = cur[id]  || 0;
        const prevVal = prev[id] || 0;
        const diff    = prevVal > 0 ? ((curVal - prevVal) / prevVal) * 100 : null;
        return { id, curVal, prevVal, diff };
      })
      .filter(r => r.curVal > 0 || r.prevVal > 0)
      .sort((a, b) => b.curVal - a.curVal)
      .slice(0, 6);

    const hasData = rows.some(r => r.prevVal > 0);
    return { rows, prevMonthLabel, hasData };
  }, [state.transactions, currentMonth]);

  const cats = useMemo(() => Object.fromEntries(state.categories.map(c => [c.id, c])), [state.categories]);

  if (rows.length === 0) return null;

  return (
    <div className="dash-card" style={{ padding: '18px 20px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div className="dash-card-title">Comparativo com Mês Anterior</div>
          <div className="dash-card-sub">
            {hasData ? `Despesas este mês vs ${prevMonthLabel}` : 'Despesas por categoria este mês'}
          </div>
        </div>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#c9a22718', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="fas fa-chart-bar" style={{ color: '#c9a227', fontSize: 15 }}></i>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map(({ id, curVal, prevVal, diff }) => {
          const cat     = cats[id];
          const isNew   = prevVal === 0;
          const isGone  = curVal  === 0;
          const pctAbs  = diff !== null ? Math.abs(diff) : 0;
          const up      = diff !== null && diff > 0;
          const neutral = diff === null || Math.abs(diff) < 2;

          const badgeColor = isNew ? '#378ADD' : isGone ? '#6b7280' : neutral ? '#6b7280' : up ? '#e24b4a' : '#1d9e75';
          const badgeBg    = isNew ? '#dbeafe'  : isGone ? '#f3f4f6' : neutral ? '#f3f4f6' : up ? '#fee2e2' : '#d1fae5';

          const maxVal = Math.max(curVal, prevVal);

          return (
            <div key={id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {cat?.name || id}
                </span>

                {/* Badge de variação */}
                <span style={{ fontSize: 10, fontWeight: 700, color: badgeColor, background: badgeBg, borderRadius: 99, padding: '2px 7px', flexShrink: 0 }}>
                  {isNew  ? 'NOVO' :
                   isGone ? 'SEM GASTO' :
                   neutral ? '=' :
                   <>{up ? '▲' : '▼'} {pctAbs.toFixed(0)}%</>}
                </span>

                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', flexShrink: 0 }}>
                  {formatCurrency(curVal)}
                </span>
              </div>

              {/* Barras comparativas */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Mês atual */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 9, color: 'var(--muted)', width: 28, textAlign: 'right', flexShrink: 0 }}>Atual</span>
                  <div style={{ flex: 1, height: 6, background: 'var(--surface2)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: maxVal > 0 ? `${(curVal / maxVal) * 100}%` : '0%', background: up && !isNew ? '#e24b4a' : '#1d9e75', borderRadius: 99, transition: 'width 0.6s ease' }} />
                  </div>
                </div>
                {/* Mês anterior */}
                {prevVal > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 9, color: 'var(--muted)', width: 28, textAlign: 'right', flexShrink: 0 }}>Ant.</span>
                    <div style={{ flex: 1, height: 6, background: 'var(--surface2)', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(prevVal / maxVal) * 100}%`, background: 'var(--muted)', opacity: 0.4, borderRadius: 99 }} />
                    </div>
                    <span style={{ fontSize: 10, color: 'var(--muted)', flexShrink: 0 }}>{formatCurrency(prevVal)}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeeklySummaryCard() {
  const { state } = useApp();

  const { weekIncome, weekExpense, prevIncome, prevExpense } = useMemo(() => {
    const now = new Date(); now.setHours(23, 59, 59, 999);
    const d7  = new Date(); d7.setDate(d7.getDate() - 7);   d7.setHours(0, 0, 0, 0);
    const d14 = new Date(); d14.setDate(d14.getDate() - 14); d14.setHours(0, 0, 0, 0);
    let weekIncome = 0, weekExpense = 0, prevIncome = 0, prevExpense = 0;
    state.transactions.forEach(t => {
      if (t.status !== 'paid' || t.cardId) return;
      const d = new Date(t.date + 'T00:00:00');
      if (d >= d7 && d <= now) {
        if (t.type === 'income') weekIncome += t.value;
        else weekExpense += t.value;
      } else if (d >= d14 && d < d7) {
        if (t.type === 'income') prevIncome += t.value;
        else prevExpense += t.value;
      }
    });
    return { weekIncome, weekExpense, prevIncome, prevExpense };
  }, [state.transactions]);

  const weekNet = weekIncome - weekExpense;
  const incomeDiff  = prevIncome  > 0 ? ((weekIncome  - prevIncome)  / prevIncome  * 100) : null;
  const expenseDiff = prevExpense > 0 ? ((weekExpense - prevExpense) / prevExpense * 100) : null;

  const DiffBadge = ({ pct, invertColor }) => {
    if (pct === null) return null;
    const positive = invertColor ? pct <= 0 : pct >= 0;
    return (
      <span style={{ fontSize: 10, color: positive ? '#1d9e75' : '#e24b4a', marginLeft: 4 }}>
        <i className={`fas fa-arrow-${pct >= 0 ? 'up' : 'down'}`} style={{ marginRight: 2 }}></i>
        {Math.abs(pct).toFixed(0)}%
      </span>
    );
  };

  return (
    <div className="dash-card" style={{ padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <div className="dash-card-title">Esta Semana</div>
          <div className="dash-card-sub">Últimos 7 dias (lançamentos pagos)</div>
        </div>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#378ADD18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="fas fa-calendar-week" style={{ color: '#378ADD', fontSize: 15 }}></i>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: '#1d9e7518', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-arrow-up" style={{ color: '#1d9e75', fontSize: 11 }}></i>
            </div>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Entradas</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1d9e75' }}>{formatCurrency(weekIncome)}</span>
            <DiffBadge pct={incomeDiff} invertColor={false} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: '#e24b4a18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-arrow-down" style={{ color: '#e24b4a', fontSize: 11 }}></i>
            </div>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Saídas</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#e24b4a' }}>{formatCurrency(weekExpense)}</span>
            <DiffBadge pct={expenseDiff} invertColor={true} />
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>Saldo da semana</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: weekNet >= 0 ? '#1d9e75' : '#e24b4a' }}>
            {weekNet >= 0 ? '+' : ''}{formatCurrency(weekNet)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Fluxo de Caixa diário do mês ──────────────────────────────────────────────
function CashFlowCard() {
  const { state, currentMonth } = useApp();

  const { labels, incData, expData, hasData } = useMemo(() => {
    const [y, m] = currentMonth.split('-').map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const inc = Array(daysInMonth).fill(0);
    const exp = Array(daysInMonth).fill(0);
    state.transactions
      .filter(t => t.date.startsWith(currentMonth) && !t.cardId)
      .forEach(t => {
        const d = parseInt(t.date.slice(8, 10), 10) - 1;
        if (d < 0 || d >= daysInMonth) return;
        if (t.type === 'income') inc[d] += t.value;
        else exp[d] += t.value;
      });
    const labels = Array.from({ length: daysInMonth }, (_, i) => `${String(i + 1).padStart(2, '0')}/${String(m).padStart(2, '0')}`);
    const hasData = inc.some(v => v > 0) || exp.some(v => v > 0);
    return { labels, incData: inc, expData: exp, hasData };
  }, [state.transactions, currentMonth]);

  if (!hasData) return null;

  const data = {
    labels,
    datasets: [
      {
        label: 'Receitas',
        data: incData,
        borderColor: '#1d9e75',
        backgroundColor: 'rgba(29,158,117,0.08)',
        fill: true, tension: 0.35, pointRadius: 2, pointHoverRadius: 5, borderWidth: 2,
      },
      {
        label: 'Despesas',
        data: expData,
        borderColor: '#e24b4a',
        backgroundColor: 'rgba(226,75,74,0.08)',
        fill: true, tension: 0.35, pointRadius: 2, pointHoverRadius: 5, borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, boxHeight: 12, font: { size: 11 }, usePointStyle: true } },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxTicksLimit: 10, font: { size: 10 } } },
      y: { beginAtZero: true, ticks: { maxTicksLimit: 5, font: { size: 10 }, callback: v => v >= 1000 ? `R$ ${(v / 1000).toFixed(1)}K` : `R$ ${v}` }, grid: { color: 'rgba(128,128,128,0.08)' } },
    },
  };

  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 12 }}>
        <i className="fas fa-chart-line" style={{ color: 'var(--gold)', marginRight: 8 }}></i>
        Fluxo de Caixa — {monthNames[parseInt(currentMonth.slice(5), 10) - 1]}
      </div>
      <div style={{ height: 200 }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
}

// ── Previsto vs Realizado do mês ──────────────────────────────────────────────
function MonthForecastCard() {
  const { state, totalBalance, currentMonth } = useApp();

  const monthTx = useMemo(
    () => state.transactions.filter(t => t.date.startsWith(currentMonth) && !t.cardId),
    [state.transactions, currentMonth]
  );

  const incPaid    = monthTx.filter(t => t.type === 'income'  && t.status === 'paid').reduce((s, t) => s + t.value, 0);
  const incTotal   = monthTx.filter(t => t.type === 'income').reduce((s, t) => s + t.value, 0);
  const expPaid    = monthTx.filter(t => t.type === 'expense' && t.status === 'paid').reduce((s, t) => s + t.value, 0);
  const expTotal   = monthTx.filter(t => t.type === 'expense').reduce((s, t) => s + t.value, 0);

  const bills      = monthTx.filter(t => t.type === 'expense');
  const billsPaid  = bills.filter(t => t.status === 'paid').length;
  const billsPct   = bills.length > 0 ? Math.round((billsPaid / bills.length) * 100) : 0;

  // Saldo projetado: saldo atual + o que ainda vai entrar - o que ainda vai sair
  const projected = totalBalance + (incTotal - incPaid) - (expTotal - expPaid);

  if (monthTx.length === 0) return null;

  const Bar = ({ paid, total, color }) => {
    const pct = total > 0 ? Math.min(100, (paid / total) * 100) : 0;
    return (
      <div style={{ height: 6, borderRadius: 99, background: 'var(--surface2)', overflow: 'hidden', marginTop: 6 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width 0.4s ease' }} />
      </div>
    );
  };

  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>
          <i className="fas fa-bullseye" style={{ color: 'var(--gold)', marginRight: 8 }}></i>
          Previsto vs Realizado — {monthNames[parseInt(currentMonth.slice(5), 10) - 1]}
        </div>
        {bills.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span><strong style={{ color: 'var(--text)' }}>{billsPaid} de {bills.length}</strong> contas pagas</span>
            <span style={{ background: billsPct === 100 ? 'rgba(29,158,117,0.12)' : 'var(--surface2)', color: billsPct === 100 ? 'var(--green)' : 'var(--text2)', borderRadius: 20, padding: '2px 10px', fontWeight: 700, fontSize: 11 }}>{billsPct}%</span>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Receitas</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--green)', marginTop: 4 }}>{formatCurrency(incPaid)}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>previsto: {formatCurrency(incTotal)}</div>
          <Bar paid={incPaid} total={incTotal} color="var(--green)" />
        </div>

        <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Despesas</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--red)', marginTop: 4 }}>{formatCurrency(expPaid)}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>previsto: {formatCurrency(expTotal)}</div>
          <Bar paid={expPaid} total={expTotal} color="var(--red)" />
        </div>

        <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Saldo projetado (fim do mês)</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: projected >= 0 ? 'var(--green)' : 'var(--red)', marginTop: 4 }}>{formatCurrency(projected)}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>saldo atual + pendências do mês</div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { state, dispatch, totalBalance, monthIncome, monthExpense, currentMonth } = useApp();
  const [showModal, setShowModal] = useState(null);

  const cats = useMemo(() => Object.fromEntries(state.categories.map(c => [c.id, c])), [state.categories]);

  const [rates, setRates] = useState(null);
  useEffect(() => {
    fetch('https://economia.awesomeapi.com.br/last/USD-BRL,EUR-BRL')
      .then(r => r.json())
      .then(d => {
        const usd = parseFloat(d?.USDBRL?.bid);
        const eur = parseFloat(d?.EURBRL?.bid);
        if (!isNaN(usd) && !isNaN(eur)) {
          setRates({ usd, eur, time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) });
        }
      })
      .catch(() => {});
  }, []);

  const catExpenses = useMemo(() => {
    const map = {};
    state.transactions
      .filter(t => t.date.startsWith(currentMonth) && t.type === 'expense' && t.status === 'paid')
      .forEach(t => { map[t.categoryId] = (map[t.categoryId] || 0) + t.value; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [state.transactions, currentMonth]);

  const recent = useMemo(() =>
    [...state.transactions].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 8),
    [state.transactions]
  );

  const catColors = ['#0d1b2a','#c9a227','#1d9e75','#e24b4a','#378ADD','#993556'];
  const totalCatExp = catExpenses.reduce((s, [, v]) => s + v, 0);

  const catBgColors = { alim:'#FAEEDA', transp:'#E6F1FB', moradia:'#FCEBEB', saude:'#FCEBEB', sal:'#EAF3DE', free:'#EAF3DE', inv:'#EAF3DE', contas:'#E6F1FB', lazer:'#FBEAF0', cartao:'#FBEAF0', educ:'#EAF3DE', default:'#F1EFE8' };
  const catIconColors = { alim:'#854F0B', transp:'#185FA5', moradia:'#e24b4a', saude:'#e24b4a', sal:'#3B6D11', free:'#3B6D11', inv:'#3B6D11', contas:'#378ADD', lazer:'#993556', cartao:'#A32D2D', educ:'#3B6D11', default:'#888780' };

  return (
    <div className="main-content">
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Visão geral das suas finanças</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <MonthSelector />
          <button className="btn-outline" onClick={() => setShowModal('transfer')}>
            <i className="fas fa-arrow-right-arrow-left"></i> Transferir
          </button>
          <button className="btn-primary-new" onClick={() => setShowModal('expense')}>
            <i className="fas fa-plus"></i> Novo Lançamento
          </button>
        </div>
      </div>

      {/* Exchange rates */}
      {rates && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'Dólar', symbol: 'USD', value: rates.usd, flag: '🇺🇸' },
            { label: 'Euro',  symbol: 'EUR', value: rates.eur, flag: '🇪🇺' },
          ].map(r => (
            <div key={r.symbol} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '7px 14px', boxShadow: 'var(--shadow)' }}>
              <span style={{ fontSize: 16 }}>{r.flag}</span>
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{r.label} ({r.symbol})</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>R$ {r.value.toFixed(2)}</div>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', fontSize: 10, color: 'var(--muted)' }}>
            <i className="fas fa-circle" style={{ fontSize: 6, color: '#22c55e' }}></i> Atualizado às {rates.time}
          </div>
        </div>
      )}

      {/* Metric cards */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-card-top">
            <span className="metric-card-label">Saldo Atual</span>
            <div className="metric-card-icon green">
              <i className="fas fa-scale-balanced"></i>
            </div>
          </div>
          <div className="metric-card-value green">{formatCurrency(totalBalance)}</div>
          <div className="metric-card-footer">Saldo consolidado de todas as contas</div>
        </div>

        <div className="metric-card">
          <div className="metric-card-top">
            <span className="metric-card-label">Total de Entradas</span>
            <div className="metric-card-icon green">
              <i className="fas fa-arrow-up"></i>
            </div>
          </div>
          <div className="metric-card-value green">{formatCurrency(monthIncome)}</div>
          <div className="metric-card-footer">Receitas de {monthNames[parseInt(currentMonth.slice(5), 10) - 1]}</div>
        </div>

        <div className="metric-card">
          <div className="metric-card-top">
            <span className="metric-card-label">Total de Saídas</span>
            <div className="metric-card-icon red">
              <i className="fas fa-arrow-down"></i>
            </div>
          </div>
          <div className="metric-card-value red">{formatCurrency(monthExpense)}</div>
          <div className="metric-card-footer">Despesas de {monthNames[parseInt(currentMonth.slice(5), 10) - 1]}</div>
        </div>
      </div>

      {/* Previsto vs Realizado */}
      <div style={{ marginBottom: 16 }}>
        <MonthForecastCard />
      </div>

      {/* Fluxo de Caixa diário */}
      <div style={{ marginBottom: 16 }}>
        <CashFlowCard />
      </div>

      {/* Esta semana */}
      <div style={{ marginBottom: 16 }}>
        <WeeklySummaryCard />
      </div>

      {/* Meta + Health + Projection */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
        <SavingsGoalWidget />
        <FinancialHealth />
        <Projection />
      </div>

      {/* Auto Insights */}
      <div style={{ marginBottom: 16 }}>
        <AutoInsights />
      </div>

      {/* Comparativo mês anterior */}
      <MonthComparisonCard />

      {/* Streak + Conquistas */}
      <GamificationCard />

      {/* Bottom row */}
      <div className="dashboard-row">
        {/* Despesas por categoria */}
        <div className="dash-card">
          <div className="dash-card-header">
            <div>
              <div className="dash-card-title">Despesas por Categoria</div>
              <div className="dash-card-sub">Distribuição dos seus gastos</div>
            </div>
          </div>

          {catExpenses.length === 0 ? (
            <div className="dash-empty">
              <i className="fas fa-chart-pie" style={{ fontSize: 32, marginBottom: 8, color: '#d1d5db' }}></i>
              <div>Sem despesas registradas</div>
            </div>
          ) : (
            <div className="cat-layout">
              <div style={{ width: 180, height: 180, flexShrink: 0 }}>
                <Doughnut
                  data={{
                    labels: catExpenses.map(([id]) => cats[id]?.name || id),
                    datasets: [{ data: catExpenses.map(([, v]) => v), backgroundColor: catColors, borderWidth: 3, borderColor: 'var(--card-bg)' }],
                  }}
                  options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + formatCurrency(ctx.parsed) } } }, cutout: '65%' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                {catExpenses.map(([id, val], i) => (
                  <div key={id} className="cat-row">
                    <span className="cat-dot" style={{ background: catColors[i % catColors.length] }}></span>
                    <span className="cat-name">{cats[id]?.name || id}</span>
                    <span className="cat-pct">{Math.round(val / totalCatExp * 100)}%</span>
                    <span className="cat-val">{formatCurrency(val)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lançamentos recentes */}
        <div className="dash-card">
          <div className="dash-card-header">
            <div>
              <div className="dash-card-title">Lançamentos Recentes</div>
              <div className="dash-card-sub">Últimas movimentações</div>
            </div>
            <button className="link-btn" onClick={() => dispatch({ type: 'SET_TAB', payload: 'transactions' })}>Ver todos</button>
          </div>

          {recent.length === 0 ? (
            <div className="dash-empty">
              <div>Nenhum lançamento registrado</div>
            </div>
          ) : (
            <div className="tx-list">
              {recent.map(tx => {
                const cat = cats[tx.categoryId];
                return (
                  <div className="tx-row" key={tx.id}>
                    <div className="tx-icon-wrap" style={{ background: catBgColors[tx.categoryId] || catBgColors.default }}>
                      <i className={cat?.icon || 'fas fa-circle'} style={{ color: catIconColors[tx.categoryId] || catIconColors.default, fontSize: 14 }}></i>
                    </div>
                    <div className="tx-info-col">
                      <div className="tx-desc-new">{tx.description}</div>
                      <div className="tx-meta">{cat?.name} · {formatDate(tx.date + 'T12:00:00')}</div>
                    </div>
                    <div className={`tx-val ${tx.type === 'income' ? 'green' : 'red'}`}>
                      {tx.type === 'income' ? '+' : '-'}{formatMoney(tx.value, tx.currency)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {(showModal === 'income' || showModal === 'expense') && (
        <TransactionModal defaultType={showModal} onClose={() => setShowModal(null)} />
      )}
      {showModal === 'transfer' && <TransferModal onClose={() => setShowModal(null)} />}
    </div>
  );
}
